import { mimeTypeToFileType } from '@modules/common/constants';
import { encodeToBase64 } from '@modules/common/utils';
import { uploadFileToURL } from '@modules/integrationsV2/utils';
import { useEffect, useRef, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { useFetchMediaUrl } from './useFetchMediaUrl';

interface useUploadMediaFileParams {
  onCompleted: (url: string) => void;
}

export const useUploadMediaFile = ({
  onCompleted,
}: useUploadMediaFileParams) => {
  const { id: userID } = useGetUser();
  const [uploadFileError, setUploadFileError] = useState('');
  const [fetchMediaUrl, { error: fetchMediaUrlError }] = useFetchMediaUrl();
  const [fileURL, setFileURL] = useState<string>();
  const [s3Key, setS3Key] = useState<string>();
  const [isCancelled, setIsCancelled] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const xhrRequest = useRef<XMLHttpRequest>(new XMLHttpRequest());

  const cancelFileUpload = () => {
    setIsCancelled(true);
  };

  useEffect(() => {
    if (isCancelled && xhrRequest.current.readyState !== 0) {
      xhrRequest.current.abort();

      setIsUploading(false);
      setIsCancelled(false);
    }
  }, [isCancelled]);

  const uploadMediaFile = async (file: File) => {
    setIsUploading(true);
    setFileURL(undefined);
    setS3Key(undefined);

    const { s3Key, url } = await fetchMediaUrl({
      fileName: encodeToBase64(file.name),
      fileType: mimeTypeToFileType[file.type],
    });

    if (!s3Key || !url) {
      cancelFileUpload();
      return;
    }

    setFileURL(url);
    setS3Key(s3Key);

    try {
      await uploadFileToURL({
        file,
        userID,
        setUploadProgress: setProgress,
        xhr: xhrRequest.current,
        url,
        shouldSkipEncoding: true,
      });

      onCompleted(s3Key);
    } catch (error) {
      setUploadFileError('Failed to upload file.');
    } finally {
      setIsUploading(false);
    }
  };

  return [
    uploadMediaFile,
    {
      fileURL,
      s3Key,
      progress,
      isUploading,
      error: fetchMediaUrlError?.message || uploadFileError,
    },
  ] as const;
};
